/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch } from 'react-redux';
import moment from 'moment-timezone';
import EmailEditor, { EditorRef, EmailEditorProps } from 'react-email-editor';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faLocationDot, faPen } from '@fortawesome/pro-regular-svg-icons';
import BaseModal from 'components/UI/Modal/BaseModal';
import Button from 'components/UI/V3/Button/Button';
import Spinner from 'components/UI/V3/Spinner/Spinner';
import { ConfirmDialog } from 'components/UI/ConfirmDialog/ConfirmDialog';
import { useOrganizationId } from 'hooks/useOrganizationId';
import { EmailEditorConfig, EmailTemplateDetail } from 'model/emailMarketing';
import {
    fetchEmailEditorConfigThunk,
    fetchEmailTemplateDetailThunk,
    saveEmailTemplateThunk
} from 'actions/emailMarketing/EmailMarketingActions';
import { useEmailMarketing } from '../EmailMarketingProvider';
import { FilterPill } from '../UI/FilterPill/FilterPill';
import styles from './TemplateEditor.module.scss';

// Design type taken from the SDK signature, as in the marketplace editor.
type UnlayerDesign = Parameters<NonNullable<EditorRef['editor']>['loadDesign']>[0];

// The backend still requires a category; same default as the marketplace editor.
const DEFAULT_CATEGORY = 'engagement';

// Unlayer expects merge tags keyed by name: { first_name: { name, value } }.
const toUnlayerMergeTags = (mergeTags: EmailEditorConfig['mergeTags']) =>
    mergeTags.reduce<Record<string, { name: string; value: string }>>((acc, tag) => {
        const key = tag.value.replace(/[{}]/g, '').trim();
        if (key) acc[key] = { name: tag.name, value: tag.value };
        return acc;
    }, {});

interface TemplateEditorProps {
    // null creates a new template.
    templateId: number | null;
    // Owner location suggested for a new template.
    defaultLocationId: number | null;
    onClose: () => void;
}

// Unlayer editor for location templates. Adapted from the marketplace
// EmailTemplateEditorComponent, using the Organization Portal endpoints (CUD-5781).
export const TemplateEditor: React.FC<TemplateEditorProps> = ({ templateId, defaultLocationId, onClose }) => {
    const dispatch = useDispatch();
    const organizationId = useOrganizationId();
    const { locations, locationName, notify } = useEmailMarketing();
    const isEdit = templateId !== null;

    const editorRef = useRef<EditorRef | null>(null);
    const [config, setConfig] = useState<EmailEditorConfig | null>(null);
    const [detail, setDetail] = useState<EmailTemplateDetail | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [attempt, setAttempt] = useState(0);
    const [editorReady, setEditorReady] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [saving, setSaving] = useState(false);
    const [confirmingExit, setConfirmingExit] = useState(false);

    const [name, setName] = useState(isEdit ? '' : `Template - ${moment().format('MMMM D, YYYY, h:mm A')}`);
    const [nameError, setNameError] = useState(false);
    const [locationId, setLocationId] = useState<number | null>(defaultLocationId);

    // Editor config, plus the template in edit mode. A failure shows a retry instead of spinning forever.
    useEffect(() => {
        if (!organizationId) return;
        let cancelled = false;
        setLoadFailed(false);
        Promise.all([
            dispatch(fetchEmailEditorConfigThunk(organizationId) as any) as Promise<EmailEditorConfig>,
            isEdit
                ? (dispatch(
                      fetchEmailTemplateDetailThunk(organizationId, templateId as number) as any
                  ) as Promise<EmailTemplateDetail>)
                : Promise.resolve(null)
        ])
            .then(([nextConfig, nextDetail]) => {
                if (cancelled) return;
                setConfig(nextConfig);
                if (nextDetail) {
                    setDetail(nextDetail);
                    setName(nextDetail.name);
                    setLocationId(nextDetail.locationId);
                }
            })
            .catch(() => {
                if (!cancelled) setLoadFailed(true);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [organizationId, templateId, attempt]);

    // Push the saved design once both the editor and the template are ready.
    useEffect(() => {
        const editor = editorRef.current?.editor;
        if (!editorReady || !editor || !detail?.designJson) return;
        try {
            editor.loadDesign(detail.designJson as unknown as UnlayerDesign);
        } catch (error) {
            console.error('[TemplateEditor] Failed to load design', error);
        }
    }, [editorReady, detail]);

    const options = useMemo<EmailEditorProps['options']>(
        () =>
            config
                ? {
                      user: { id: config.userId, ...(config.signature ? { signature: config.signature } : {}) },
                      mergeTags: toUnlayerMergeTags(config.mergeTags)
                  }
                : {},
        [config]
    );

    const handleReady: EmailEditorProps['onReady'] = unlayer => {
        // Only real edits count, so leaving an untouched template doesn't ask to discard.
        unlayer.addEventListener('design:updated', () => setDirty(true));
        setEditorReady(true);
    };

    const handleSave = () => {
        if (!name.trim()) {
            setNameError(true);
            return;
        }
        const editor = editorRef.current?.editor;
        if (!editor || !organizationId || (!isEdit && !locationId)) return;

        setSaving(true);
        editor.exportHtml(data => {
            const input = {
                name: name.trim(),
                html: data.html,
                designJson: data.design as unknown as Record<string, unknown>,
                category: detail?.category ?? DEFAULT_CATEGORY
            };
            const target = isEdit ? { templateId: templateId as number } : { locationId: locationId as number };
            (dispatch(saveEmailTemplateThunk(organizationId, input, target) as any) as Promise<void>)
                .then(() => {
                    notify('Template saved');
                    onClose();
                })
                .catch(() => setSaving(false));
        });
    };

    const requestClose = () => (dirty ? setConfirmingExit(true) : onClose());

    const renderBody = () => {
        if (loadFailed) {
            return (
                <div className={styles.state}>
                    <strong>The editor couldn&apos;t be loaded</strong>
                    <span>Check your connection and try again.</span>
                    <Button
                        variant="black"
                        label="Try again"
                        onClick={() => setAttempt(prev => prev + 1)}
                    />
                </div>
            );
        }
        if (config && !config.projectId) {
            return (
                <div className={styles.state}>
                    <strong>The email editor isn&apos;t configured</strong>
                    <span>Contact support to enable template editing.</span>
                </div>
            );
        }
        if (!config || (isEdit && !detail)) {
            return (
                <div className={styles.state}>
                    <Spinner />
                </div>
            );
        }
        return (
            <div className={styles.editorHost}>
                <EmailEditor
                    ref={editorRef}
                    projectId={config.projectId ?? undefined}
                    options={options}
                    onReady={handleReady}
                    minHeight="100%"
                    style={{ flex: 1 }}
                />
            </div>
        );
    };

    return (
        <BaseModal
            isOpen
            onClose={requestClose}
        >
            <BaseModal.Header showDefaultLayout={false}>
                <div className={styles.header}>
                    <div className={styles.headerLeft}>
                        <label className={styles.nameField}>
                            <input
                                value={name}
                                maxLength={100}
                                // Grows with the name so long default names aren't cut off.
                                size={Math.min(Math.max(name.length, 12), 48)}
                                aria-label="Template name"
                                aria-invalid={nameError}
                                onChange={event => {
                                    setName(event.target.value);
                                    setNameError(false);
                                    setDirty(true);
                                }}
                            />
                            <FontAwesomeIcon icon={faPen} />
                        </label>
                        {nameError && <span className={styles.error}>Name is required</span>}

                        {/* A template belongs to one location; it's chosen when creating and fixed afterwards. */}
                        {isEdit ? (
                            <span className={styles.locationTag}>
                                <FontAwesomeIcon icon={faLocationDot} /> {locationName(locationId)}
                            </span>
                        ) : (
                            <FilterPill
                                icon={faLocationDot}
                                label={locationId ? locationName(locationId) : 'Choose location'}
                            >
                                {close => (
                                    <ul className={styles.locationList}>
                                        {locations.map(location => (
                                            <li key={location.id}>
                                                <button
                                                    type="button"
                                                    className={
                                                        location.id === locationId ? styles.locationOn : undefined
                                                    }
                                                    onClick={() => {
                                                        setLocationId(location.id);
                                                        close();
                                                    }}
                                                >
                                                    {location.name}
                                                    <small>{location.city}</small>
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </FilterPill>
                        )}
                    </div>
                    <div className={styles.actions}>
                        <Button
                            variant="outlined"
                            label="Cancel"
                            disabled={saving}
                            onClick={requestClose}
                        />
                        <Button
                            variant="black"
                            label={saving ? 'Saving…' : 'Save template'}
                            disabled={saving || !editorReady || (!isEdit && !locationId)}
                            onClick={handleSave}
                        />
                    </div>
                </div>
            </BaseModal.Header>

            {renderBody()}

            <p className={styles.mobileNotice}>Template editing is available on desktop and tablet.</p>

            <ConfirmDialog
                open={confirmingExit}
                title="Discard changes?"
                question="Your unsaved changes will be lost."
                cancelButtonLabel="Keep editing"
                confirmButtonLabel="Discard"
                onCancel={() => setConfirmingExit(false)}
                onConfirm={onClose}
            />
        </BaseModal>
    );
};

export default TemplateEditor;
